import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Spinner, Button, Form, InputGroup, Tabs, Tab, Badge, Modal, ListGroup } from 'react-bootstrap';
import {
  searchCommunityMembersApi,
  getCommunityMembersApi,
  getPendingRequestsApi,
  approveRequestApi,
  rejectRequestApi,
  removeMembershipApi,
  addMembersApi
} from '../../../api/communities';
import { searchStudentsApi } from '../../../api/events';

export const CommunityMembers = ({ communityId, isOwner, showOnly }) => {
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(showOnly || 'members');

  // Add Member Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (showOnly !== 'pending') {
      fetchMembers();
    }
    if (isOwner || showOnly === 'pending') {
      fetchPendingRequests();
    }
  }, [communityId, isOwner, showOnly]);

  const fetchMembers = async (q = '') => {
    try {
      setLoading(true);
      let res;
      if (q) {
        res = await searchCommunityMembersApi({ communityId, search: q });
      } else {
        res = await getCommunityMembersApi(communityId, 'approved');
      }
      setMembers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch members', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const res = await getPendingRequestsApi(communityId);
      setPendingRequests(res.data || []);
    } catch (err) {
      console.error('Failed to fetch pending requests', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMembers(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    fetchMembers('');
  };

  const handleApprove = async (userId) => {
    try {
      await approveRequestApi(communityId, userId);
      // Remove from pending and reload members
      setPendingRequests(prev => prev.filter(req => req.user?._id !== userId));
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    try {
      await rejectRequestApi(communityId, userId);
      setPendingRequests(prev => prev.filter(req => req.user?._id !== userId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject');
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await removeMembershipApi(communityId, userId);
      setMembers(prev => prev.filter(m => m.user?._id !== userId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleSearchUsersToAdd = async (q) => {
    setAddSearchQuery(q);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      setSearching(true);
      const res = await searchStudentsApi({ search: q });
      setSearchResults(res.data || []);
    } catch (err) {
      console.error('Failed to search users', err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await addMembersApi(communityId, userId);
      alert('Member added successfully!');
      fetchMembers(); // Reload members list
      setShowAddModal(false);
      setAddSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add member');
    }
  };

  const renderMembersList = () => {
    if (loading) {
      return <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>;
    }
    if (members.length === 0) {
      return (
        <div className="text-center py-5 text-muted bg-white rounded-3 border-dashed">
          <i className="bi bi-people fs-1 mb-2"></i>
          <h5>No members found</h5>
        </div>
      );
    }
    return (
      <Row className="g-3">
        {members.map((member) => {
          const userId = member.user?._id;
          const userType = member.user?.user_type || 'student';
          const profileLink = userType === 'teacher' ? `/teachers/${userId}` : `/student/${userId}`;

          return (
            <Col md={6} lg={4} key={member._id}>
              <Card className="shadow-sm border rounded-3 h-100 member-card">
                <Card.Body className="d-flex align-items-center gap-3">
                  <Link to={profileLink} className="d-flex align-items-center gap-3 text-decoration-none text-dark flex-grow-1 min-w-0">
                    <img
                      src={member.user?.profile_picture || 'https://via.placeholder.com/60'}
                      alt="Avatar"
                      className="user-avatar-lg"
                      style={{ width: '60px', minWidth: '60px', height: '60px', objectFit: 'cover', borderRadius: '50%' }}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <div className="fs-6 fw-bold text-truncate">{member.user?.full_name || 'Unknown User'}</div>
                      <div className="text-muted text-sm">Joined {new Date(member.createdAt).toLocaleDateString()}</div>
                    </div>
                  </Link>
                  {isOwner && (
                    <Button variant="outline-danger" size="sm" className="rounded-pill mt-1 px-3 py-0 flex-shrink-0" style={{ fontSize: '0.75rem' }} onClick={() => handleRemove(userId)}>
                      Remove
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderPendingList = () => {
    if (pendingRequests.length === 0) {
      return (
        <div className="text-center py-5 text-muted bg-white rounded-3 border-dashed">
          <i className="bi bi-person-check fs-1 mb-2"></i>
          <h5>No pending requests</h5>
        </div>
      );
    }
    return (
      <Row className="g-3">
        {pendingRequests.map((req) => {
          const userId = req.user?._id;
          const userType = req.user?.user_type || 'student';
          const profileLink = userType === 'teacher' ? `/teachers/${userId}` : `/student/${userId}`;

          return (
            <Col md={6} lg={4} key={req._id}>
              <Card className="shadow-sm border-0 border-start border-4 border-warning rounded-3 h-100 member-card">
                <Card.Body>
                  <Link to={profileLink} className="d-flex align-items-center gap-3 mb-3 text-decoration-none text-dark">
                    <img
                      src={req.user?.profile_picture || 'https://via.placeholder.com/60'}
                      alt="Avatar"
                      className="rounded-circle object-fit-cover"
                      style={{ width: '50px', height: '50px' }}
                    />
                    <div className="min-w-0">
                      <h6 className="mb-0 fw-bold text-truncate" style={{ maxWidth: '150px' }}>{req.user?.full_name || 'Unknown User'}</h6>
                      <small className="text-muted text-xs">Requested: {new Date(req.createdAt).toLocaleDateString()}</small>
                    </div>
                  </Link>
                  <div className="d-flex gap-2">
                    <Button variant="success" size="sm" className="w-50 rounded-pill fw-medium" onClick={() => handleApprove(userId)}>Approve</Button>
                    <Button variant="outline-danger" size="sm" className="w-50 rounded-pill fw-medium" onClick={() => handleReject(userId)}>Reject</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderMembersSearch = () => (
    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
      <h5 className="mb-0 fw-bold"><i className="bi bi-people-fill me-2 text-primary"></i>Community Members</h5>
      <div className="d-flex gap-3 align-items-center">
        <Form onSubmit={handleSearch} style={{ width: '300px' }}>
          <InputGroup className="bg-white border rounded-pill shadow-sm overflow-hidden px-2 py-1">
            <InputGroup.Text className="bg-transparent border-0"><i className="bi bi-search text-muted"></i></InputGroup.Text>
            <Form.Control
              placeholder="Search members..."
              className="border-0 shadow-none bg-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button variant="link" className="text-muted p-0 pe-2 text-decoration-none shadow-none" onClick={clearSearch}>
                <i className="bi bi-x-circle-fill"></i>
              </Button>
            )}
          </InputGroup>
        </Form>
        {isOwner && (
          <Button variant="primary" className="rounded-pill px-4" onClick={() => setShowAddModal(true)}>
            <i className="bi bi-person-plus-fill me-2"></i>Add Member
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="community-members py-3 mx-auto">
      {showOnly === 'pending' ? (
        renderPendingList()
      ) : showOnly === 'members' ? (
        <>
          {renderMembersSearch()}
          {renderMembersList()}
        </>
      ) : isOwner ? (
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4 border-bottom-0 custom-pills"
          variant="pills"
        >
          <Tab eventKey="members" title={<><i className="bi bi-people-fill me-2"></i>Active Members</>}>
            {renderMembersSearch()}
            {renderMembersList()}
          </Tab>
          <Tab
            eventKey="pending"
            title={
              <>
                <i className="bi bi-person-plus-fill me-2"></i>Pending Requests
                {pendingRequests.length > 0 && <Badge bg="danger" className="ms-2 rounded-pill">{pendingRequests.length}</Badge>}
              </>
            }
          >
            {renderPendingList()}
          </Tab>
        </Tabs>
      ) : (
        <>
          {renderMembersSearch()}
          {renderMembersList()}
        </>
      )}

      {/* Add Member Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Member to Community</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search by name or email..."
              value={addSearchQuery}
              onChange={(e) => handleSearchUsersToAdd(e.target.value)}
              autoFocus
            />
          </Form.Group>

          {searching ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : searchResults.length > 0 ? (
            <ListGroup variant="flush" className="border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {searchResults.map(user => (
                <ListGroup.Item key={user._id} className="d-flex justify-content-between align-items-center p-3">
                  <div className="d-flex align-items-center gap-3">
                    <img src={user.profile_picture || 'https://via.placeholder.com/40'} alt="Avatar" className="rounded-circle" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                    <div>
                      <div className="fw-bold">{user.full_name}</div>
                      <div className="small text-muted">{user.email}</div>
                    </div>
                  </div>
                  <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={() => handleAddMember(user._id)}>
                    Add
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : addSearchQuery.trim() ? (
            <div className="text-center text-muted py-3">No users found.</div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};